import React, { useState, useEffect } from 'react';
import './DocumentViewer.css';

export default function DocumentViewer({ driverId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!driverId) {
            setError('No Driver ID provided.');
            setLoading(false);
            return;
        }

        fetch('/driver_api/documents', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ driverId })
        })
        .then(res => res.json())
        .then(res => {
            if (res.success) {
                setData(res.data);
            } else {
                setError(res.message || 'Failed to fetch documents.');
            }
            setLoading(false);
        })
        .catch(err => {
            setError('Error connecting to server.');
            setLoading(false);
        });
    }, [driverId]);

    if (loading) {
        return (
            <div className="doc-viewer-loader">
                <div className="doc-spinner"></div>
                <p>Loading documents securely...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="doc-viewer-error">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>{error}</p>
            </div>
        );
    }

    if (!data) return null;

    const renderDocuments = (docs, title) => {
        if (!docs || docs.length === 0) {
            return <div className="no-docs"><p>No {title.toLowerCase()} available.</p></div>;
        }

        return (
            <div className="doc-section">
                <h3 className="section-title">{title}</h3>
                <div className="doc-grid">
                    {docs.map((doc, idx) => {
                        const fileUrl = `/api/uploads/${doc.file}`;
                        const isPdf = doc.file && doc.file.toLowerCase().endsWith('.pdf');
                        return (
                            <div key={idx} className="doc-card">
                                <div className="doc-preview">
                                    {isPdf ? (
                                        <div className="pdf-placeholder">
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                                <polyline points="10 9 9 9 8 9"></polyline>
                                            </svg>
                                            <span>PDF Document</span>
                                        </div>
                                    ) : (
                                        <img src={fileUrl} alt={doc.name} className="img-preview" />
                                    )}
                                </div>
                                <div className="doc-info">
                                    <p className="doc-name" title={doc.name}>{doc.name}</p>
                                    <a href={fileUrl} target="_blank" rel="noreferrer" className="view-btn">
                                        View Full Document
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft: '6px'}}>
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="doc-viewer-wrapper">
            <div className="doc-viewer-container">
                <header className="doc-header">
                    <div className="doc-header-content">
                        <h2>Document Repository</h2>
                        <p>Driver: <strong>{data.driver.name}</strong> {data.driver.driverCode ? `(${data.driver.driverCode})` : ''}</p>
                    </div>
                    <div className="doc-header-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                        </svg>
                    </div>
                </header>
                
                <div className="doc-content">
                    {renderDocuments(data.driver?.documents, 'Driver Documents')}
                    
                    {data.vehicle && (
                        <>
                            <div className="doc-divider"></div>
                            <div className="vehicle-info-bar">
                                <div className="vehicle-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="7" width="20" height="10" rx="2" ry="2"></rect>
                                        <circle cx="5.5" cy="17.5" r="2.5"></circle>
                                        <circle cx="18.5" cy="17.5" r="2.5"></circle>
                                    </svg>
                                </div>
                                <div className="vehicle-details">
                                    <h4>Vehicle Information</h4>
                                    <p><strong>{data.vehicle.make} {data.vehicle.model}</strong> • {data.vehicle.vehicleNumber}</p>
                                </div>
                            </div>
                            {renderDocuments(data.vehicle?.documents, 'Vehicle Documents')}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
